/**
 * Audio generation for Supernal TTS Widget
 */

import { TTSResponse } from '../types';

export class AudioGenerator {
  private apiUrl: string;
  private apiKey?: string;

  constructor(apiUrl: string, apiKey?: string) {
    this.apiUrl = apiUrl;
    this.apiKey = apiKey;
  }

  /**
   * Generate audio using standard API call
   */
  public async generateAudio(text: string, options: any): Promise<TTSResponse> {
    const body = {
      text,
      options: {
        provider: options.provider,
        voice: options.voice,
        speed: options.speed || 1.0
      }
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    // Use widget-specific API key if provided, otherwise use global
    const apiKey = options.apiKey || this.apiKey;
    console.log('[TTS Widget] Generate Audio - API Key present:', !!apiKey);
    console.log('[TTS Widget] Generate Audio - this.apiKey:', !!this.apiKey);
    console.log('[TTS Widget] Generate Audio - options.apiKey:', !!options.apiKey);
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
      console.log('[TTS Widget] Generate Audio - Authorization header added');
    } else {
      console.warn('[TTS Widget] Generate Audio - NO API KEY, Authorization header NOT added');
    }

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        audioUrl: `${this.apiUrl}${data.audioUrl}`,
        hash: data.hash,
        cached: data.cached,
        duration: data.duration,
        cost: data.cost
      };
    } catch (error) {
      // Log the actual error for debugging
      console.error('TTS generation failed:', error);

      // Re-throw with more context but preserve the original error
      if ((error as Error).message.includes('Failed to fetch') || (error as Error).message.includes('NetworkError')) {
        const originalMessage = (error as Error).message;
        throw new Error(`TTS API unreachable: ${originalMessage}. Check if ${this.apiUrl} is accessible.`);
      }

      // Re-throw other errors as-is
      throw error;
    }
  }

  /**
   * Generate audio using progressive SSE streaming
   * Updates progress bar in real-time as chunks complete
   */
  public async generateAudioProgressive(
    text: string,
    options: any,
    button: HTMLButtonElement,
    progressBar: HTMLElement | null | undefined,
    onStateChange: (state: string) => void,
    onProgress?: (completed: number, total: number) => void
  ): Promise<{ audioUrl: string; hash: string; duration?: number; cost?: number }> {
    const body = {
      text,
      options: {
        provider: options.provider,
        voice: options.voice,
        speed: options.speed || 1.0
      }
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    };

    const apiKey = options.apiKey || this.apiKey;
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/generate-progressive`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body for SSE stream');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let totalChunks = 0;
      let completedChunks = 0;

      // Update progress bar if available
      const updateProgress = (completed: number, total: number) => {
        if (progressBar) {
          const percentage = (completed / total) * 100;
          const slider = progressBar.querySelector<HTMLInputElement>('.supernal-tts-progress-slider');
          if (slider) {
            slider.value = percentage.toString();
          }

          const currentSpan = progressBar.querySelector<HTMLElement>('.current');
          if (currentSpan) {
            currentSpan.textContent = `Chunk ${completed}/${total}`;
          }
        }

        if (onProgress) {
          onProgress(completed, total);
        }
      };

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          try {
            const event = JSON.parse(line.substring(6));

            switch (event.type) {
              case 'started':
                totalChunks = event.totalChunks;
                console.log(`[Progressive TTS] Started: ${totalChunks} chunks`);
                onStateChange('loading');
                break;

              case 'progress':
                completedChunks = event.completed;
                console.log(`[Progressive TTS] Progress: ${completedChunks}/${totalChunks}`);
                updateProgress(completedChunks, totalChunks);
                break;

              case 'complete':
                console.log(`[Progressive TTS] Complete:`, event);
                const audioUrl = event.audioUrl.startsWith('http')
                  ? event.audioUrl
                  : `${this.apiUrl}${event.audioUrl}`;

                return {
                  audioUrl,
                  hash: event.hash,
                  duration: event.duration,
                  cost: event.cost
                };

              case 'error':
                throw new Error(event.message || 'Progressive generation failed');
            }
          } catch (parseError) {
            console.warn('[Progressive TTS] Failed to parse event:', line, parseError);
          }
        }
      }

      throw new Error('Progressive generation completed without final event');
    } catch (error) {
      console.error('Progressive TTS generation failed:', error);
      throw error;
    }
  }

  /**
   * Update API key
   */
  public setApiKey(apiKey: string | undefined): void {
    this.apiKey = apiKey;
  }
}
